import type { DownloadFullReportRequestDto } from 'shared/dtos/reports.dto';
import type { Person } from 'shared/interfaces/people.interface';

import { Button } from 'components/buttons/button/button';
import { CheckboxField } from 'components/fields/checkbox-field/checkbox-field';
import { Container } from 'components/template/container/container';
import { FixedHeader } from 'components/template/fixed-header/fixed-header';
import { FormBody } from 'components/template/form-body/form-body';
import { FormHeader } from 'components/template/form-header/FormHeader';
import { Spinner } from 'components/template/spinner/spinner';
import { PermissionsContext } from 'contexts';
import { Form, Formik } from 'formik';
import { useContext, useEffect, useMemo, useState } from 'react';
import ReportService from 'services/reports.service';
import { CONFIG_FORM } from 'shared/constants/configForm';
import events from 'shared/constants/events';
import CheckIfFormIsDirty from 'shared/utils/checkIfFormIsDirty';
import { vocabulary } from 'vocabulary/german';

const reportsService = new ReportService();
const initialValues: Person = { id: null, name: '' };

export const ConfigFullForm = () => {
  const { permissions } = useContext(PermissionsContext);
  const [configForm, setConfigForm] = useState<typeof CONFIG_FORM>(CONFIG_FORM);
  const { reports, headerCheckbox } = configForm;
  const [policiesCheck, agenciesCheck, divisionsCheck] = headerCheckbox;
  const [loadingReport, setLoadingReport] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const isShowReportButton = useMemo(
    () => policiesCheck.checked || agenciesCheck.checked,
    [agenciesCheck.checked, policiesCheck.checked],
  );

  const renderReportButton = (type: 'pdf' | 'docx') => {
    if (permissions.printReport === true) {
      return (
        <Button
          text={
            type === 'pdf'
              ? vocabulary.buttons.reports.downloadReportPdf
              : vocabulary.buttons.reports.downloadReportDocx
          }
          onClick={() => downloadConfigReport(type)}
        />
      );
    }
  };

  const downloadConfigReport = (type: 'pdf' | 'docx') => {
    const dataToReport = {} as DownloadFullReportRequestDto;

    Object.entries(reports).forEach(([key, report]) => {
      dataToReport[key] = {
        elements: Object.fromEntries(
          report.elements.map(({ id, checked }) => [id, checked]),
        ),
        subElements: Object.fromEntries(
          report.subElements.map(({ id, checked }) => [id, checked]),
        ),
      };
    });

    dataToReport.isShowAgencies = agenciesCheck.checked;
    dataToReport.isShowPolicies = policiesCheck.checked;

    setLoadingReport(true);
    reportsService.downloadFullReport(type, dataToReport);
  };

  const onChangeHeaderCheckbox = (event, id) => {
    const { checked } = event.target;

    const newHeaders = headerCheckbox.map((header) =>
      header.id === id
        ? { ...header, checked }
        : { ...header, checked: !checked && header.checked },
    );

    const newReports = {
      ...reports,
      [id]: {
        ...reports[id],
        elements: reports[id].elements.map((item) => ({
          ...item,
          checked: item.checked || checked,
        })),
        subElements: reports[id].subElements.map((item) => ({
          ...item,
          checked: item.checked && checked,
        })),
      },
      divisions: {
        ...reports.divisions,
        subElements:
          id === 'agencies'
            ? reports.divisions.subElements.map((item) => ({
                ...item,
                checked: item.checked && checked,
              }))
            : reports.divisions.subElements,
      },
    };

    setConfigForm({ headerCheckbox: newHeaders, reports: newReports });
  };

  const onChangeElement = (block, type, currentIndex?) => {
    const curBlock = reports[block];
    const stgIndex = 1;

    const newReports = {
      ...reports,
      [block]: {
        ...curBlock,
        [type]: curBlock[type].map((item, index) => {
          if (
            type === 'subElements' &&
            currentIndex < stgIndex + 1 &&
            index > currentIndex
          ) {
            return { ...item, checked: false };
          }
          return {
            ...item,
            checked: currentIndex === index ? !item.checked : item.checked,
          };
        }),
      },
    };

    const newHeaders = headerCheckbox.map((header) => {
      if (header.id === block) {
        return {
          ...header,
          checked: newReports[block].elements.some((report) => report.checked),
        };
      }
      return header;
    });

    setConfigForm({ headerCheckbox: newHeaders, reports: newReports });
  };

  useEffect(() => {
    const unlock = (): void => setLoadingReport(false);
    document.body.addEventListener(events.UNLOCK.name, unlock);

    return () => {
      document.body.removeEventListener(events.UNLOCK.name, unlock);
    };
  }, []);

  useEffect(() => {
    const { agencies, policies, divisions } = reports;

    reportsService.getConfigReport().then((data) => {
      const checkedData = { agencies, policies };

      if (data) {
        Object.keys(data).forEach((key) => {
          const curData = data[key];
          checkedData[key].elements = curData.map((item) => ({
            ...item,
            checked: true,
          }));
        });
      }

      setConfigForm((prev) => ({
        ...prev,
        reports: { ...checkedData, divisions },
      }));
      setLoading(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={() => console.log('submit form')}
    >
      {({ dirty }) => (
        <Form>
          <CheckIfFormIsDirty dirty={dirty} />
          <FixedHeader>
            {isShowReportButton && (
              <FormHeader
                renderReport={renderReportButton('pdf')}
                renderDocxReport={renderReportButton('docx')}
              />
            )}
          </FixedHeader>
          <FormBody>
            <Container className="full-report__content">
              <div
                className={`full-report__block ${agenciesCheck.checked ? 'full-report__block--disabled' : ''}`}
              >
                <CheckboxField
                  key={policiesCheck.id}
                  id={policiesCheck.id}
                  isHeader
                  text={policiesCheck.title}
                  disabled={agenciesCheck.checked}
                  checked={policiesCheck.checked}
                  onChangeCheckbox={(e) =>
                    onChangeHeaderCheckbox(e, policiesCheck.id)
                  }
                />
                {policiesCheck.checked && (
                  <div className="content">
                    {reports.policies.elements.map((policy, index) => (
                      <CheckboxField
                        key={`policies-elem-${policy.id}`}
                        id={`policies-elem-${policy.id}`}
                        text={policy.title}
                        checked={policy.checked}
                        onChangeCheckbox={() =>
                          onChangeElement('policies', 'elements', index)
                        }
                      />
                    ))}
                    <hr />
                    {reports.policies.subElements.map(
                      (policy, index, allPolicies) =>
                        index === 0 ? (
                          <CheckboxField
                            key={`policies-${policy.id}`}
                            id={`policies-${policy.id}`}
                            text={policy.title}
                            checked={policy.checked}
                            onChangeCheckbox={() =>
                              onChangeElement('policies', 'subElements', index)
                            }
                          />
                        ) : (
                          (allPolicies[index - 1].checked ||
                            allPolicies[1].checked) && (
                            <CheckboxField
                              key={`policies-${policy.id}`}
                              id={`policies-${policy.id}`}
                              text={policy.title}
                              checked={policy.checked}
                              onChangeCheckbox={() =>
                                onChangeElement(
                                  'policies',
                                  'subElements',
                                  index,
                                )
                              }
                            />
                          )
                        ),
                    )}
                  </div>
                )}
              </div>
              <div
                className={`full-report__block ${policiesCheck.checked ? 'full-report__block--disabled' : ''}`}
              >
                <CheckboxField
                  key={agenciesCheck.id}
                  id={agenciesCheck.id}
                  isHeader
                  text={agenciesCheck.title}
                  checked={agenciesCheck.checked}
                  disabled={policiesCheck.checked}
                  onChangeCheckbox={(e) =>
                    onChangeHeaderCheckbox(e, agenciesCheck.id)
                  }
                />
                {agenciesCheck.checked && (
                  <>
                    <div className="content">
                      {reports.agencies.elements.map((agency, index) => (
                        <CheckboxField
                          key={`agencies-elem-${agency.id}`}
                          id={`agencies-elem-${agency.id}`}
                          text={agency.title}
                          checked={agency.checked}
                          onChangeCheckbox={() =>
                            onChangeElement('agencies', 'elements', index)
                          }
                        />
                      ))}
                      <hr />
                      {reports.agencies.subElements.map(
                        (agency, index, allAgencies) =>
                          index === 0 ? (
                            <CheckboxField
                              key={`agencies-${agency.id}`}
                              id={`agencies-${agency.id}`}
                              text={agency.title}
                              checked={agency.checked}
                              onChangeCheckbox={() =>
                                onChangeElement(
                                  'agencies',
                                  'subElements',
                                  index,
                                )
                              }
                            />
                          ) : (
                            allAgencies[index - 1].checked && (
                              <CheckboxField
                                key={`agencies-${agency.id}`}
                                id={`agencies-${agency.id}`}
                                text={agency.title}
                                checked={agency.checked}
                                onChangeCheckbox={() =>
                                  onChangeElement(
                                    'agencies',
                                    'subElements',
                                    index,
                                  )
                                }
                              />
                            )
                          ),
                      )}
                    </div>
                    <div className="full-report__divisions">
                      <div className="form__checkbox-field_header">
                        <span className="form__checkbox-text_header">
                          {divisionsCheck.title}
                        </span>
                      </div>
                      <div className="content">
                        {reports.divisions.subElements.map(
                          (division, index, allDivisions) =>
                            index === 0 ? (
                              <CheckboxField
                                key={`${vocabulary.labels.divisions}-${division.id}`}
                                id={`${vocabulary.labels.divisions}-${division.id}`}
                                text={division.title}
                                checked={division.checked}
                                onChangeCheckbox={() =>
                                  onChangeElement(
                                    'divisions',
                                    'subElements',
                                    index,
                                  )
                                }
                              />
                            ) : (
                              (allDivisions[index - 1].checked ||
                                allDivisions[1].checked) && (
                                <CheckboxField
                                  key={`${vocabulary.labels.divisions}-${division.id}`}
                                  id={`${vocabulary.labels.divisions}-${division.id}`}
                                  text={division.title}
                                  checked={division.checked}
                                  onChangeCheckbox={() =>
                                    onChangeElement(
                                      'divisions',
                                      'subElements',
                                      index,
                                    )
                                  }
                                />
                              )
                            ),
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </Container>
          </FormBody>
          {loadingReport && (
            <Spinner isFullSize message={vocabulary.preloader.waitForReport} />
          )}
          {loading && <Spinner isFullSize />}
        </Form>
      )}
    </Formik>
  );
};
